import { useEffect, useRef } from 'react';

export function usePhoneDialog(panel, onClose, active = true, closeDisabled = false, restoreFocus, closeOnEscape = true) {
    useEffect(() => {
        const media = window.matchMedia?.('(max-width: 767px)');
        if (!active || !panel.current || !media || panel.current.closest('.ia-comms')) return;
        let restore;
        function sync() {
            restore?.(); restore = undefined;
            if (!media.matches) return;
            const previousFocus = document.activeElement;
            const overflow = document.body.style.overflow;
            document.body.style.overflow = 'hidden';
            panel.current.querySelector('.ia-modal-close')?.focus();
            restore = () => { document.body.style.overflow = overflow; if (restoreFocus) restoreFocus(previousFocus); else previousFocus?.focus?.(); };
        }
        sync();
        media.addEventListener?.('change', sync);
        return () => { media.removeEventListener?.('change', sync); restore?.(); };
    }, [active, panel, restoreFocus]);
    function onKeyDown(event) {
        if (!active) return;
        if (event.defaultPrevented || !window.matchMedia?.('(max-width: 767px)')?.matches || panel.current.closest('.ia-comms')) return;
        if (event.key === 'Escape') { event.stopPropagation(); if (!closeOnEscape) event.preventDefault(); else if (!closeDisabled) onClose(); }
        if (event.key !== 'Tab') return;
        const controls = [...panel.current.querySelectorAll('button:not(:disabled), input:not(:disabled), select:not(:disabled), textarea:not(:disabled), a[href]')]
            .filter(control => control.getClientRects().length);
        const first = controls[0], last = controls[controls.length - 1];
        if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
        else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
    }
    return onKeyDown;
}

/** True while the shared phone full-screen dialog is in force for this panel (the phone hook then owns focus, Escape and Tab). */
export function isPhoneDialog(panel) {
    return Boolean(panel?.current && window.matchMedia?.('(max-width: 767px)')?.matches && !panel.current.closest('.ia-comms'));
}

// closeOnEscape is independent of closeDisabled: legacy forms can keep their
// original no-Escape behavior without disabling explicit close buttons or scrims.
// Retains each caller's desktop surface; the phone layout is shared in global.css.
// BL-177 Step 2j: `role` (alertdialog), `restoreFocus` (#656's focus-restoration callback, passed to the phone hook), `panelRef`, `panelProps` / `overlayProps` (data attributes, extra handlers) are optional so the
// platform popup template (ui/Modal3D) can sit on this one surface instead of making a second dialog system. Existing callers are unchanged.
export default function ModalSurface({ children, onClose, label, labelledBy, closeDisabled = false, closeOnEscape = true, busy, panelClassName, overlayClassName = "fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50", panelStyle, onOverlayClick, role = 'dialog', panelRef, panelProps, overlayProps, restoreFocus, phoneFullScreen = true }) {
    const ownRef = useRef(null);
    const panel = panelRef || ownRef;
    useEffect(() => {
        const node = panel.current, viewport = window.visualViewport;
        // M7: only opted-in staff panels; preserve Comms and mouse positioning.
        if (!node || !viewport || node.closest('.ia-comms, .ia-events') || node.dataset.m6Component?.startsWith('Event') || !node.matches('[data-m6-layout], [data-m7-touch], .ia-financials, .ia-settings-popup, .ia-schedule-details')) return;
        const overlay = node.parentElement;
        const original = { top: overlay.style.top, height: overlay.style.height, maxHeight: node.style.maxHeight };
        const restore = () => { overlay.style.top = original.top; overlay.style.height = original.height; node.style.maxHeight = original.maxHeight; };
        const sync = () => {
            restore();
            if (!window.matchMedia?.('(min-width: 768px) and (pointer: coarse)').matches) return;
            overlay.style.top = `${viewport.offsetTop}px`;
            overlay.style.height = `${viewport.height}px`;
            node.style.maxHeight = `${Math.max(44, viewport.height - 48)}px`;
        };
        sync();
        viewport.addEventListener('resize', sync);
        viewport.addEventListener('scroll', sync);
        window.addEventListener('resize', sync);
        return () => { viewport.removeEventListener('resize', sync); viewport.removeEventListener('scroll', sync); window.removeEventListener('resize', sync); restore(); };
    }, [panel]);
    // phoneFullScreen={false}: a small pop-up that stays a centred card on a phone; its owner keeps focus, Escape and Tab.
    const onPhoneKeyDown = usePhoneDialog(panel, onClose, phoneFullScreen, closeDisabled, restoreFocus, closeOnEscape);
    const { onKeyDown: extraKeyDown, ...restPanelProps } = panelProps || {};
    const onKeyDown = event => { onPhoneKeyDown(event); extraKeyDown?.(event); };
    return <div data-mobile-modal data-phone-card={phoneFullScreen ? undefined : 'true'} className={overlayClassName} onClick={onOverlayClick} {...overlayProps}>
        <div ref={panel} role={role} aria-modal="true" aria-busy={busy} aria-label={labelledBy ? undefined : label} aria-labelledby={labelledBy} className={panelClassName} style={panelStyle} onClick={onOverlayClick ? event => event.stopPropagation() : undefined} {...restPanelProps} onKeyDown={onKeyDown}>
            <button hidden disabled={closeDisabled} type="button" className="ia-modal-close" aria-label={`Close ${label}`} onClick={onClose}>×</button>
            {children}
        </div>
    </div>;
}
